import { useMemo } from 'react'

import { PreFillType } from '@constants'
import { FormType } from '@types'

type UsePreFillConfigParams = {
  fetchedForm?: FormType
  preFillFromAnotherObject?: PreFillType
}

type PreFillConfig = {
  hasPreFillField: boolean
  preFill: PreFillType | undefined
  isFormReady: boolean
}

/**
 * Хук для определения конфигурации предварительного заполнения формы
 *
 * @returns {PreFillConfig}
 * - hasPreFillField: есть ли в форме поля с preFill
 * - preFill: данные для предзаполнения (если нужны)
 * - isFormReady: готова ли форма к использованию
 */
export const usePreFillConfig = ({
  fetchedForm,
  preFillFromAnotherObject,
}: UsePreFillConfigParams): PreFillConfig => {
  const isFormReady = Boolean(fetchedForm?.fields)

  const hasPreFillField = useMemo(
    () => Boolean(fetchedForm?.fields?.some(field => field.preFill)),
    [fetchedForm?.fields]
  )

  const preFill = useMemo((): PreFillType | undefined => {
    // Если в форме нет полей с preFill, то preFill не нужен
    if (!hasPreFillField) {
      return undefined
    }

    // Если есть предварительное заполнение из другого объекта (родительского), используем его
    if (preFillFromAnotherObject) {
      return preFillFromAnotherObject
    }

    // Иначе используем пустые параметры для предзаполнения по форме
    return {
      objectCode: undefined,
      objectId: undefined,
    }
  }, [hasPreFillField, preFillFromAnotherObject])

  return {
    hasPreFillField,
    preFill,
    isFormReady,
  }
}
